import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { SEED_CATEGORIES, SEED_SERVICES } from '../constants/masterSeedData';
import { DEFAULT_DURATION, DEFAULT_GENDER } from '../constants/masterData';

const BATCH_LIMIT = 400; // Firestore hard limit is 500 writes per batch

const normalizeName = (name) => (name || '').trim().toLowerCase();

const sortCategories = (a, b) =>
  (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.name || '').localeCompare(b.name || '');

/**
 * Fetch all master categories, sorted by sortOrder then name.
 * @returns {Promise<Array<{id, name, nameLower, enabled, sortOrder}>>}
 */
export async function fetchMasterCategories() {
  const snapshot = await getDocs(collection(db, 'masterCategories'));
  const categories = [];
  snapshot.forEach((d) => categories.push({ id: d.id, ...d.data() }));
  categories.sort(sortCategories);
  return categories;
}

/**
 * Fetch all master services, sorted by name.
 * @returns {Promise<Array<{id, name, nameLower, categoryId, categoryName, gender, defaultDuration, subServices, enabled}>>}
 */
export async function fetchMasterServices() {
  const snapshot = await getDocs(collection(db, 'masterServices'));
  const services = [];
  snapshot.forEach((d) => services.push({ id: d.id, ...d.data() }));
  services.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  return services;
}

export async function addMasterCategory({ name, sortOrder }) {
  const trimmed = (name || '').trim();
  const ref = await addDoc(collection(db, 'masterCategories'), {
    name: trimmed,
    nameLower: normalizeName(trimmed),
    enabled: true,
    sortOrder: Number(sortOrder) || 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return ref.id;
}

export async function updateMasterCategory(id, { name, sortOrder }) {
  const trimmed = (name || '').trim();
  await updateDoc(doc(db, 'masterCategories', id), {
    name: trimmed,
    nameLower: normalizeName(trimmed),
    sortOrder: Number(sortOrder) || 0,
    updatedAt: serverTimestamp()
  });
}

export async function setMasterCategoryEnabled(id, enabled) {
  await updateDoc(doc(db, 'masterCategories', id), {
    enabled: !!enabled,
    updatedAt: serverTimestamp()
  });
}

/**
 * @param {Object} service - { name, categoryId, categoryName, gender, defaultDuration, subServices }
 * @returns {Promise<string>} new doc id
 */
export async function addMasterService(service) {
  const trimmed = (service.name || '').trim();
  const ref = await addDoc(collection(db, 'masterServices'), {
    name: trimmed,
    nameLower: normalizeName(trimmed),
    categoryId: service.categoryId || '',
    categoryName: service.categoryName || '',
    gender: service.gender || DEFAULT_GENDER,
    defaultDuration: Number(service.defaultDuration) || DEFAULT_DURATION,
    subServices: (service.subServices || [])
      .filter((s) => (s.name || '').trim())
      .map((s) => ({
        name: s.name.trim(),
        defaultDuration: Number(s.defaultDuration) || DEFAULT_DURATION
      })),
    enabled: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return ref.id;
}

export async function updateMasterService(id, service) {
  const trimmed = (service.name || '').trim();
  await updateDoc(doc(db, 'masterServices', id), {
    name: trimmed,
    nameLower: normalizeName(trimmed),
    categoryId: service.categoryId || '',
    categoryName: service.categoryName || '',
    gender: service.gender || DEFAULT_GENDER,
    defaultDuration: Number(service.defaultDuration) || DEFAULT_DURATION,
    subServices: (service.subServices || [])
      .filter((s) => (s.name || '').trim())
      .map((s) => ({
        name: s.name.trim(),
        defaultDuration: Number(s.defaultDuration) || DEFAULT_DURATION
      })),
    updatedAt: serverTimestamp()
  });
}

export async function setMasterServiceEnabled(id, enabled) {
  await updateDoc(doc(db, 'masterServices', id), {
    enabled: !!enabled,
    updatedAt: serverTimestamp()
  });
}

async function commitInChunks(buildWrite, items) {
  for (let i = 0; i < items.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    items.slice(i, i + BATCH_LIMIT).forEach((item) => buildWrite(batch, item));
    await batch.commit();
  }
}

/**
 * Idempotently seed masterCategories and masterServices from the seed data.
 * Existing names (case-insensitive) are skipped; for services that already
 * exist, any NEW subservices from the seed are merged in (never removed).
 * Safe to run repeatedly.
 * @returns {Promise<{categoriesAdded: number, servicesAdded: number, servicesUpdated: number}>}
 */
export async function seedMasterData() {
  const existingCategories = await fetchMasterCategories();
  const existingCategoryNames = new Set(existingCategories.map((c) => c.nameLower || normalizeName(c.name)));

  const newCategories = SEED_CATEGORIES.filter((c) => !existingCategoryNames.has(normalizeName(c.name)));
  await commitInChunks((batch, cat) => {
    batch.set(doc(collection(db, 'masterCategories')), {
      name: cat.name,
      nameLower: normalizeName(cat.name),
      enabled: true,
      sortOrder: cat.sortOrder,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }, newCategories);

  // Re-fetch to map categoryName -> categoryId for the services being seeded
  const allCategories = newCategories.length ? await fetchMasterCategories() : existingCategories;
  const categoryIdByName = {};
  allCategories.forEach((c) => {
    categoryIdByName[c.nameLower || normalizeName(c.name)] = c.id;
  });

  const existingServices = await fetchMasterServices();
  const existingByName = new Map(
    existingServices.map((s) => [s.nameLower || normalizeName(s.name), s])
  );

  const newServices = SEED_SERVICES.filter((s) => !existingByName.has(normalizeName(s.name)));
  await commitInChunks((batch, svc) => {
    batch.set(doc(collection(db, 'masterServices')), {
      name: svc.name,
      nameLower: normalizeName(svc.name),
      categoryId: categoryIdByName[normalizeName(svc.categoryName)] || '',
      categoryName: svc.categoryName,
      gender: svc.gender || DEFAULT_GENDER,
      defaultDuration: svc.defaultDuration || DEFAULT_DURATION,
      subServices: (svc.subServices || []).map((sub) => ({
        name: sub.name,
        defaultDuration: sub.defaultDuration || DEFAULT_DURATION
      })),
      enabled: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }, newServices);

  // Merge any subservices the seed gained since the service was first seeded
  // (e.g. new colour variants under "Hair Coloring"). Existing subservices
  // and any admin edits are left untouched — this only appends missing names.
  const subServiceMerges = [];
  SEED_SERVICES.forEach((seed) => {
    if (!seed.subServices || !seed.subServices.length) return;
    const existing = existingByName.get(normalizeName(seed.name));
    if (!existing) return; // freshly added above with full subservices
    const currentSubs = existing.subServices || [];
    const currentSubNames = new Set(currentSubs.map((s) => normalizeName(s.name)));
    const missingSubs = seed.subServices.filter((sub) => !currentSubNames.has(normalizeName(sub.name)));
    if (missingSubs.length) {
      subServiceMerges.push({
        id: existing.id,
        subServices: [
          ...currentSubs,
          ...missingSubs.map((sub) => ({
            name: sub.name,
            defaultDuration: sub.defaultDuration || DEFAULT_DURATION
          }))
        ]
      });
    }
  });
  await commitInChunks((batch, merge) => {
    batch.update(doc(db, 'masterServices', merge.id), {
      subServices: merge.subServices,
      updatedAt: serverTimestamp()
    });
  }, subServiceMerges);

  return {
    categoriesAdded: newCategories.length,
    servicesAdded: newServices.length,
    servicesUpdated: subServiceMerges.length
  };
}
